import { PropsWithChildren, useEffect, useState } from 'react';
import { useKeycloak } from 'all-in-one-react-keycloak';
import { useTranslation } from 'react-i18next';
import { Form, Alert, FormInstance } from 'antd';
import { PrimaryButton } from 'components';
import { UserMigrationDetails } from 'all-in-one-shared-api/dist/apis/user/usePatchExistingUser';

import { StretchedSpace } from '.';
import { revalidateAntdFormLanguageChanged } from '../../../i18n';

const { useForm } = Form;

interface FormWrapperProps {
  onSubmit: (formValues: UserMigrationDetails) => Promise<void>;
  error: string | undefined;
  handleCloseSnackbar: () => void;
  initialValues?: Record<string, unknown>;
  isLoading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customForm?: FormInstance<any>;
}

const WeCareFormWrapper = ({
  initialValues,
  onSubmit,
  error,
  handleCloseSnackbar,
  isLoading,
  children,
  customForm,
}: PropsWithChildren<FormWrapperProps>) => {
  const { keycloak } = useKeycloak();
  const { t, i18n } = useTranslation();
  const [snackbarMessage, setSnackbarMessage] = useState(error || '');

  keycloak.onAuthError = (err) => {
    setIsSubmissionLoading(false);
    if (err?.error) {
      setSnackbarMessage(err.error);
    }
  };

  const [isSubmissionLoading, setIsSubmissionLoading] = useState<boolean>(false);

  const [myForm] = useForm();

  const form = customForm ?? myForm;

  useEffect(() => {
    revalidateAntdFormLanguageChanged(form);
  }, [i18n.language, form]);

  const submitForm = () => {
    form
      .validateFields()
      .then(() => {
        form.submit();
        setIsSubmissionLoading(true);
      })
      .catch((err) => {
        setIsSubmissionLoading(false);
        console.log(err);
      });
  };

  const onFinish = (formValues: UserMigrationDetails) => {
    try {
      onSubmit(formValues);
    } catch (err) {
      setIsSubmissionLoading(false);
      console.log(err);
    }
  };

  return (
    <Form
      initialValues={initialValues}
      form={form}
      layout="vertical"
      onFinish={onFinish}
      requiredMark={false}
      validateTrigger="onSubmit"
    >
      {snackbarMessage && (
        <Alert
          message={t(snackbarMessage)}
          type="error"
          closable
          onClose={handleCloseSnackbar}
          style={{ marginBottom: 16 }}
        />
      )}
      <StretchedSpace size={16} direction="vertical">
        {children}
        <Form.Item shouldUpdate>
          <PrimaryButton loading={isSubmissionLoading && isLoading} onClick={submitForm}>
            {t('common.next')}
          </PrimaryButton>
        </Form.Item>
      </StretchedSpace>
    </Form>
  );
};

export default WeCareFormWrapper;
